import { AppConfig } from '@core/domain/AppConfig.ts';
import type { HttpTransportPort } from '@core/domain/ports/driven/HttpTransportPort.ts';
import type { PuntosEntregaApplicationService } from '@src/modules/puntos_entrega/application/PuntosEntregaApplicationService.ts';
import type { RedencionBonoApplicationService } from '@src/modules/redencion_bono/application/RedencionBonoApplicationService.ts';
import { AuthStorageAdapter } from '@src/modules/auth/infrastructure/driven/AuthStorageAdapter.ts';
import { CatalogDownloadApplicationService } from '@src/modules/sync/application/CatalogDownloadApplicationService.ts';
import { OfflineSyncApplicationService } from '@src/modules/sync/application/OfflineSyncApplicationService.ts';
import type { CatalogDownloadPort } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { OfflineSyncPort } from '@src/modules/sync/domain/ports/OfflineSyncPort.ts';
import { applyMasterDataJsonResponseToDb } from '@src/modules/sync/infrastructure/driven/applyMasterDataJsonResponseToDb.ts';
import { getMasterDataUpdate } from '@src/modules/sync/infrastructure/driven/getMasterDataUpdate.ts';
import {
  getLastMasterCatalogRevision,
  setLastMasterCatalogRevision,
} from '@src/modules/sync/infrastructure/driven/masterCatalogRevisionStorage.ts';
import { postOfflineSyncPayload } from '@src/modules/sync/infrastructure/driven/postOfflineSyncPayload.ts';
import { isSnapshotRevisionUpgradePending } from '@src/modules/shared/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import { assertLikelyReachableForSync } from '@src/modules/sync/infrastructure/driven/syncConnectivityPreCheck.ts';
import { markOfflineUploadCompleteInDb } from '@src/modules/sync/infrastructure/driven/sqlJsMarkOfflineUploadComplete.ts';
import { createSyncPdfResolver } from '@src/modules/sync/infrastructure/driven/createSyncPdfResolver.ts';
import type { AppContainerDeps } from './AppContainer.ts';

export type SyncServicesWiring = {
  offlineSync: OfflineSyncPort;
  catalogDownload: CatalogDownloadPort;
};

type BuildSyncServicesInput = {
  deps: AppContainerDeps;
  authStorage: AuthStorageAdapter;
  httpTransport: HttpTransportPort;
  puntosEntregaShell: PuntosEntregaApplicationService;
  redencionBonoApplication: RedencionBonoApplicationService;
};

/** Wires offline sync upload and master-catalog download services. */
export function buildSyncServices(input: BuildSyncServicesInput): SyncServicesWiring {
  const preflightReachability = (url: string) =>
    assertLikelyReachableForSync(url, AppConfig.resolveSyncConnectivityProbeUrl());

  const pdfResolver = createSyncPdfResolver({
    puntosEntregaShell: input.puntosEntregaShell,
    redencionBonoApplication: input.redencionBonoApplication,
  });

  const resolveOfflineSyncBearer = () =>
    AppConfig.resolveSyncBearerForOfflineTransport(input.authStorage.getToken());

  const offlineSync = new OfflineSyncApplicationService(
    input.puntosEntregaShell,
    input.redencionBonoApplication,
    () => AppConfig.resolveSyncUploadUrl(),
    (url, payload, onProgress) =>
      postOfflineSyncPayload(
        url,
        payload,
        input.httpTransport,
        onProgress,
        resolveOfflineSyncBearer(),
        AppConfig.syncUploadSendIdempotencyKeyHeader(),
      ),
    preflightReachability,
    pdfResolver,
    AppConfig.useLocalSqliteClient() && input.deps.localSqlite
      ? async (batch) => {
          const runtime = input.deps.localSqlite!;
          const db = runtime.getDb();
          if (!db) return;
          markOfflineUploadCompleteInDb(db, () => runtime.notifyPersist(), batch);
          // Flush to IndexedDB now: a debounced fire-and-forget write could silently lose the upload mark.
          await runtime.flushPersist();
        }
      : undefined,
  );

  const catalogDownload = new CatalogDownloadApplicationService(
    input.deps.localSqlite,
    () => AppConfig.resolveSyncDownloadUrl(),
    (url, token, onProgress) => getMasterDataUpdate(url, token, input.httpTransport, onProgress),
    (raw, runOptions) => {
      const db = input.deps.localSqlite?.getDb();
      if (!db) {
        throw new Error('No hay base de datos local abierta; esta acción solo aplica en modo SQLite embebido.');
      }
      return applyMasterDataJsonResponseToDb(
        db,
        raw,
        {
          getLastAppliedRevision: getLastMasterCatalogRevision,
          persistAppliedRevision: setLastMasterCatalogRevision,
        },
        {
          forceApply: runOptions?.forceApply === true || isSnapshotRevisionUpgradePending(),
          onApplyProgress: runOptions?.onApplyProgress,
        },
      );
    },
    preflightReachability,
    resolveOfflineSyncBearer,
  );

  return { offlineSync, catalogDownload };
}
